/**
 * Seeded contract boards (spec §9.3, plan §6 M2-08): every station offers `perStation` jobs per lease cycle,
 * generated from `hash32(station, cycle, careerSeed)` alone, so the same (station, cycle, seed) always gives the
 * identical board and a save only needs the accepted, completed and failed ids.
 *
 * Offer `k` of a board has the id `${station}:${cycle}:${k}` and draws every number from
 * `seededUnit(boardSeed, k, salt)`. Types are picked by weight among the types the station can issue (a
 * delivery needs a ware the station buys, a courier another station, recovery and clear raiders a marker).
 * Standing ≥ `unlockStanding` with the station's faction turns every offer into the premium variant
 * (reward × `unlockMult`); the rest of the offer does not change, so the board keeps its ids and jobs.
 * The standing an offer pays is `contractStanding`: a faction's `factionStanding` override (Holdfast: a flat +10 while
 * the player's standing with it is below 25, D-335 D4) or the seeded `standingReward` range.
 *
 * In the Reach (M3 plan §7 item 4, R22g) three more types join through the bound `ReachContractSource`
 * (`reach.ts`): lane repair (a down ring in the system), relay parts delivery (an offline relay still missing parts)
 * and chart a system (an uncharted system 1..maxHops relay hops away). They are issuable only while their list is
 * non-empty, so without a source the board is exactly M2's; with one it also follows the Reach state.
 *
 * In the Reach with M5 (S-M2-12) a bound `ExtraOfferSource` appends more offers after the seeded ones: the Charter
 * career bridge (`src/charter/careerBridge.ts`) turns the issuing faction's open `FactionRequest`s into offers. Without
 * a source the board is exactly as before.
 *
 * Career layer: pure, not per-frame (hot-loop rules do not apply).
 */
import { stationDef } from '../data/m2'
import {
  COMPONENT_TYPES,
  CONTRACT_TYPES,
  WARE_IDS,
  type ComponentType,
  type ContractType,
  type ContractsConfig,
  type M2Data,
  type SimFactionId,
  type StationDef,
  type StationId,
  type WareId,
} from '../data/m2Schemas'
import { fieldConfigSchema, worldSchema } from '../data/schemas'
import fieldJson from '../data/field.json'
import worldJson from '../data/world.json'
import { stationRadiusOf } from '../reach/session/siteRadius'
import { hash32, hashString, seededUnit } from '../space/hash'
import type { Career, ContractParams } from '../career/types'
import { reachContractSource, type ReachContractSource } from './reach'
import { hideoutJobsHeldBack } from '../story/okafor'

/**
 * M7 S13 (spec §6, W5): the Reach's system → galaxy anchor lookup (the story link binds it). A lane-ring or relay job
 * in the `okafor-hideout` system is never issued before story tier 3, so no generic contract starts Okafor's risk.
 * Unbound (M2 alone, sandbox, tests that don't bind it) nothing is held back.
 */
let systemAnchor: ((systemId: string) => string | null) | null = null

/** Binds the system → anchor lookup for the story hold-backs; null unbinds it. */
export function bindContractAnchors(lookup: ((systemId: string) => string | null) | null): void {
  systemAnchor = lookup
}

/** True when a Reach job in `systemId` is held back from boards for this career (W5). */
export function storyHeldBack(career: Career, systemId: string): boolean {
  if (systemAnchor === null) return false
  return hideoutJobsHeldBack(systemAnchor(systemId), career.storyTier ?? 0)
}

/** A job on a station's board (not yet accepted). Accepting copies it into a `ContractState`. */
export interface ContractOffer {
  /** `${station}:${cycle}:${k}`. */
  id: string
  type: ContractType
  /** Issuing station (standing goes to its faction). */
  station: StationId
  /** The lease cycle whose board issued it. */
  cycle: number
  /** Credits paid on hand-in (premium already applied). */
  reward: number
  /** Standing gained with the issuing faction on hand-in. */
  standing: number
  /** The ×`unlockMult` variant (standing ≥ `unlockStanding`). */
  premium: boolean
  /** Game seconds from acceptance to the deadline (`deadlineCycles × cycleSeconds` or `deadlineSeconds`). */
  durationSeconds: number
  params: ContractParams
}

/**
 * The spawn pocket markers stay out of (the rock-free sphere around the player spawn, `field.json pocketRadius`
 * around `world.json spawn`). Read once from the bundled data so every board, preview and accept agrees.
 */
const POCKET = {
  center: worldSchema.parse(worldJson).spawn.position,
  radius: fieldConfigSchema.parse(fieldJson).pocketRadius,
} as const

/** Marker placements tried before a marker type is dropped from an offer (an algorithm bound, not tuning). */
const MARKER_ATTEMPTS = 16

// Salts of the per-offer draws (each draw of an offer has its own salt so changing one never shifts another).
const SALT_COUNT = 1
const SALT_TYPE = 2
const SALT_WARE = 3
const SALT_TONS = 4
const SALT_COMPONENT = 5
const SALT_TIER = 6
const SALT_REWARD = 7
const SALT_STANDING = 8
const SALT_DEST = 9
const SALT_TARGET = 10
const SALT_UNITS = 11
/** Marker attempt `a` uses salts SALT_MARKER + 3a .. + 3a + 2. */
const SALT_MARKER = 100

/**
 * More offers for a board (M5 S-M2-12). Called after the station's services check; every offer id must keep the
 * `${station}:${cycle}:${key}` form (`commands.ts contractCycle` prunes by it) and must not collide with `k` ids.
 */
export type ExtraOfferSource = (data: M2Data, career: Career, station: StationDef, cycleIndex: number) => readonly ContractOffer[]

let extraOffers: ExtraOfferSource | null = null

/** Binds the extra offer source (the Charter career bridge); null unbinds it. */
export function bindExtraOffers(source: ExtraOfferSource | null): void {
  extraOffers = source
}

/** The board seed of (station, cycle, career seed). */
export function boardSeed(stationId: StationId, cycleIndex: number, careerSeed: number): number {
  return hash32(hashString(stationId), cycleIndex, careerSeed)
}

/** The sealed pod id of courier contract `contractId` (its `CargoComponent.id`). */
export function podIdOf(contractId: string): string {
  return `${contractId}:pod`
}

/** The lease cycle a board belongs to at the career's game time. */
export function boardCycle(career: Career): number {
  return Math.floor(career.gameTime / career.lease.cycleSeconds)
}

/** An integer in [lo, hi] from a unit draw. */
function intIn(u: number, lo: number, hi: number): number {
  const n = lo + Math.floor(u * (hi - lo + 1))
  return n > hi ? hi : n
}

/**
 * The standing a contract of `faction` pays on hand-in, fixed when the offer is drawn (D-335 D4, M7 spec §12.5): the
 * faction's `factionStanding` override's `flat` while `current` (the player's standing with it) is below its `until`,
 * else `standingReward` at the seeded draw `u` (rounded).
 */
export function contractStanding(cfg: ContractsConfig, faction: SimFactionId, current: number, u: number): number {
  const o = cfg.factionStanding[faction]
  if (o !== undefined && current < o.until) return o.flat
  return Math.round(lerp(cfg.standingReward[0], cfg.standingReward[1], u))
}

function lerp(lo: number, hi: number, u: number): number {
  return lo + (hi - lo) * u
}

/** Wares a station buys that a delivery may ask for (tetherstone never: it is a mystery, not a commodity). */
export function deliveryWares(station: StationDef): WareId[] {
  return WARE_IDS.filter((w) => w !== 'tetherstone' && station.prices.wares[w] !== undefined)
}

/** Component types a recovery may name (anything with a value; a cargo pod is worth only its contents). */
export function recoveryTypes(data: M2Data): ComponentType[] {
  return COMPONENT_TYPES.filter((t) => data.wares.components[COMPONENT_TYPES.indexOf(t)].baseValue > 0)
}

/** Stations a courier from `station` can go to. */
function courierDestinations(data: M2Data, station: StationDef): StationDef[] {
  return data.stations.stations.filter((s) => s.id !== station.id)
}

/**
 * A marker `distance` [min, max] off `station`'s hull (R + d from its centre, R the bounding radius of the model the
 * runtime shows, `stationRadiusOf(assetId)`; scale SC-5e1), outside the spawn pocket and at least `stationExclusion`
 * off every station's hull; null when no attempt fits.
 */
export function pickMarker(
  data: M2Data,
  station: StationDef,
  distance: readonly [number, number],
  seed: number,
  k: number,
): [number, number, number] | null {
  const minClear = data.encounters.stationExclusion
  const r = stationRadiusOf(station.assetId)
  for (let a = 0; a < MARKER_ATTEMPTS; a++) {
    const salt = SALT_MARKER + 3 * a
    const cz = 2 * seededUnit(seed, k, salt) - 1
    const phi = 2 * Math.PI * seededUnit(seed, k, salt + 1)
    const d = r + lerp(distance[0], distance[1], seededUnit(seed, k, salt + 2))
    const rxy = Math.sqrt(1 - cz * cz)
    const x = station.position[0] + d * rxy * Math.cos(phi)
    const y = station.position[1] + d * cz
    const z = station.position[2] + d * rxy * Math.sin(phi)
    if (isMarkerClear(data, x, y, z, minClear)) return [x, y, z]
  }
  return null
}

/** Outside the spawn pocket and at least `minClear` off every station's hull (R + `minClear` from its centre; SC-5e1). */
export function isMarkerClear(data: M2Data, x: number, y: number, z: number, minClear: number): boolean {
  const p = POCKET.center
  if (Math.hypot(x - p[0], y - p[1], z - p[2]) < POCKET.radius) return false
  for (const s of data.stations.stations) {
    if (Math.hypot(x - s.position[0], y - s.position[1], z - s.position[2]) - stationRadiusOf(s.assetId) < minClear) return false
  }
  return true
}

/** The spawn pocket markers avoid (for tests and debug views). */
export function markerPocket(): { center: readonly [number, number, number]; radius: number } {
  return POCKET
}

/** Picks a type by weight among the allowed ones; null when none is left. */
function pickType(data: M2Data, allowed: ReadonlySet<ContractType>, u: number): ContractType | null {
  const types = data.contracts.types
  let total = 0
  for (const t of CONTRACT_TYPES) if (allowed.has(t)) total += types[t].weight
  if (!(total > 0)) return null
  let r = u * total
  let last: ContractType | null = null
  for (const t of CONTRACT_TYPES) {
    if (!allowed.has(t) || !(types[t].weight > 0)) continue
    last = t
    r -= types[t].weight
    if (r < 0) return t
  }
  return last
}

interface Draft {
  reward: number
  durationSeconds: number
  params: ContractParams
}

/** The type-specific part of offer `k`, or null when the station cannot issue this type (no marker fits). */
/** Item `u` of a non-empty list. */
function pick<T>(list: readonly T[], u: number): T {
  return list[intIn(u, 0, list.length - 1)]
}

function draftOffer(
  data: M2Data,
  career: Career,
  station: StationDef,
  type: ContractType,
  seed: number,
  k: number,
  id: string,
  reach: ReachContractSource | null,
): Draft | null {
  const cfg = data.contracts.types
  const cycleSeconds = career.lease.cycleSeconds
  switch (type) {
    case 'laneRepair': {
      const rings = (reach?.laneRings() ?? []).filter((r) => !storyHeldBack(career, r.systemId))
      if (rings.length === 0) return null
      const ring = pick(rings, seededUnit(seed, k, SALT_TARGET))
      return {
        reward: ring.partsValue * cfg.laneRepair.rewardMult,
        durationSeconds: cfg.laneRepair.deadlineCycles * cycleSeconds,
        params: { ringId: ring.ringId, systemId: ring.systemId, targetName: ring.targetName },
      }
    }
    case 'relayParts': {
      const relays = (reach?.relays() ?? []).filter((r) => !storyHeldBack(career, r.systemId))
      if (relays.length === 0) return null
      const relay = pick(relays, seededUnit(seed, k, SALT_TARGET))
      const drawn = intIn(seededUnit(seed, k, SALT_UNITS), cfg.relayParts.units[0], cfg.relayParts.units[1])
      const units = Math.min(drawn, relay.missing)
      return {
        reward: units * relay.unitValue * cfg.relayParts.rewardMult,
        durationSeconds: cfg.relayParts.deadlineCycles * cycleSeconds,
        params: { relayId: relay.relayId, systemId: relay.systemId, units, delivered: 0, targetName: relay.targetName },
      }
    }
    case 'chartSystem': {
      const systems = reach?.charts(cfg.chartSystem.maxHops) ?? []
      if (systems.length === 0) return null
      const sys = pick(systems, seededUnit(seed, k, SALT_TARGET))
      const base = lerp(cfg.chartSystem.reward[0], cfg.chartSystem.reward[1], seededUnit(seed, k, SALT_REWARD))
      return {
        reward: base * (1 + cfg.chartSystem.hopBonus * (sys.hops - 1)),
        durationSeconds: cfg.chartSystem.deadlineCycles * cycleSeconds,
        params: { systemId: sys.systemId, targetName: sys.targetName },
      }
    }
    case 'delivery': {
      const wares = deliveryWares(station)
      const ware = wares[intIn(seededUnit(seed, k, SALT_WARE), 0, wares.length - 1)]
      const tons = intIn(seededUnit(seed, k, SALT_TONS), Math.ceil(cfg.delivery.tons[0]), Math.floor(cfg.delivery.tons[1]))
      const mult = station.prices.wares[ware] ?? 0
      const local = data.wares.wares[WARE_IDS.indexOf(ware)].basePrice * mult * tons
      return { reward: cfg.delivery.rewardMult * local, durationSeconds: cfg.delivery.deadlineCycles * cycleSeconds, params: { ware, tons } }
    }
    case 'recovery': {
      const marker = pickMarker(data, station, cfg.recovery.markerDistance, seed, k)
      if (marker === null) return null
      const kinds = recoveryTypes(data)
      const componentType = kinds[intIn(seededUnit(seed, k, SALT_COMPONENT), 0, kinds.length - 1)]
      const value = data.wares.components[COMPONENT_TYPES.indexOf(componentType)].baseValue
      return {
        reward: value * (1 + cfg.recovery.rewardBonus),
        durationSeconds: cfg.recovery.deadlineCycles * cycleSeconds,
        params: { marker, componentType },
      }
    }
    case 'clearRaiders': {
      const marker = pickMarker(data, station, cfg.clearRaiders.markerDistance, seed, k)
      if (marker === null) return null
      const tiers = data.encounters.contractTiers.length
      const tier = intIn(seededUnit(seed, k, SALT_TIER), 0, tiers - 1)
      // Harder tiers pay from the upper part of the range: tier t covers the t-th of `tiers` equal bands.
      const u = (tier + seededUnit(seed, k, SALT_REWARD)) / tiers
      return {
        reward: lerp(cfg.clearRaiders.reward[0], cfg.clearRaiders.reward[1], u),
        durationSeconds: cfg.clearRaiders.deadlineCycles * cycleSeconds,
        params: { marker, tier },
      }
    }
    case 'courier': {
      const dests = courierDestinations(data, station)
      const destination = dests[intIn(seededUnit(seed, k, SALT_DEST), 0, dests.length - 1)].id
      return {
        reward: lerp(cfg.courier.reward[0], cfg.courier.reward[1], seededUnit(seed, k, SALT_REWARD)),
        durationSeconds: cfg.courier.deadlineSeconds,
        params: { destination, podId: podIdOf(id) },
      }
    }
  }
}

/** The types `station` can issue at all (markers are checked per offer). */
function issuableTypes(data: M2Data, station: StationDef, reach: ReachContractSource | null): Set<ContractType> {
  const out = new Set<ContractType>()
  if (deliveryWares(station).length > 0) out.add('delivery')
  if (recoveryTypes(data).length > 0) out.add('recovery')
  if (data.encounters.contractTiers.length > 0) out.add('clearRaiders')
  if (courierDestinations(data, station).length > 0) out.add('courier')
  if (reach !== null) {
    if (reach.laneRings().length > 0) out.add('laneRepair')
    if (reach.relays().length > 0) out.add('relayParts')
    if (reach.charts(data.contracts.types.chartSystem.maxHops).length > 0) out.add('chartSystem')
  }
  return out
}

/**
 * The full seeded board of `stationId` for lease cycle `cycleIndex` (accepted, completed and failed offers
 * included: `board.ts` filters them). Empty when the station offers no contracts service. `reach` defaults to the
 * bound Reach source (null outside the Reach: no Reach types).
 */
export function generateBoard(
  data: M2Data,
  career: Career,
  stationId: StationId,
  cycleIndex: number,
  reach: ReachContractSource | null = reachContractSource(),
): ContractOffer[] {
  const station = stationDef(data, stationId)
  if (!station.services.includes('contracts')) return []
  const cfg = data.contracts
  const seed = boardSeed(station.id, cycleIndex, career.seed)
  const count = intIn(seededUnit(seed, 0, SALT_COUNT), cfg.perStation[0], cfg.perStation[1])
  const premium = career.standings[station.faction] >= cfg.unlockStanding
  const issuable = issuableTypes(data, station, reach)
  const offers: ContractOffer[] = []
  for (let k = 0; k < count; k++) {
    const id = `${station.id}:${cycleIndex}:${k}`
    const allowed = new Set(issuable)
    const uType = seededUnit(seed, k, SALT_TYPE)
    let type = pickType(data, allowed, uType)
    let draft: Draft | null = null
    while (type !== null) {
      draft = draftOffer(data, career, station, type, seed, k, id, reach)
      if (draft !== null) break
      allowed.delete(type)
      type = pickType(data, allowed, uType)
    }
    if (type === null || draft === null) continue
    const mult = premium ? cfg.unlockMult : 1
    offers.push({
      id,
      type,
      station: station.id,
      cycle: cycleIndex,
      reward: Math.round(draft.reward * mult),
      standing: contractStanding(cfg, station.faction, career.standings[station.faction], seededUnit(seed, k, SALT_STANDING)),
      premium,
      durationSeconds: draft.durationSeconds,
      params: draft.params,
    })
  }
  if (extraOffers !== null) for (const o of extraOffers(data, career, station, cycleIndex)) offers.push(o)
  return offers
}
